import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminUsers, updateAdminUserRole, updateAdminUserPuuid, getAdminAllPuuids, deleteAdminUser } from '../../api/senaApi';
import './SenaAuth.css';

const PUUID_PATTERN = /^[A-F0-9]{32}$/;

const ROLES = [
  { key: '', label: '전체' },
  { key: 'PENDING', label: '가입대기' },
  { key: 'USER', label: '일반' },
  { key: 'MASTER', label: '길드마스터' },
  { key: 'ADMIN', label: '관리자' },
];

const ROLE_LABELS = { PENDING: '가입대기', USER: '일반', MASTER: '길드마스터', ADMIN: '관리자' };
const ROLE_COLORS = { PENDING: '#c88050', USER: '#2060a0', MASTER: '#e8734a', ADMIN: '#c04040' };

export default function SenaAdmin() {
  const navigate = useNavigate();
  const userRole = localStorage.getItem('userRole');
  const myLoginId = localStorage.getItem('userId');
  const isAdmin = userRole === 'ADMIN';
  // 길드마스터는 가입 승인/취소(가입대기 ↔ 일반)만 가능
  const isMaster = userRole === 'MASTER';
  const [filter, setFilter] = useState('PENDING');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin && !isMaster) {
      alert('관리자만 접근 가능합니다.');
      navigate('/sena');
      return;
    }
    fetchUsers();
  }, [filter]);

  const fetchUsers = () => {
    setLoading(true);
    getAdminUsers(filter)
      .then(r => setUsers(r.data || []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  };

  // 길드마스터가 바꿀 수 있는 대상인지 (가입대기·일반 회원만)
  const canChangeRole = (user) =>
    isAdmin || ['PENDING', 'USER'].includes(user.user_role);

  const handleRoleChange = async (loginId, newRole) => {
    if (!window.confirm(`${loginId} → ${ROLE_LABELS[newRole]}(으)로 변경하시겠습니까?`)) return;
    try {
      await updateAdminUserRole(loginId, newRole);
      fetchUsers();
    } catch { alert('변경 실패'); }
  };

  const handlePuuidEdit = async (loginId, currentPuuid) => {
    const input = window.prompt(
      `${loginId}의 PUUID\n(32자 대문자/숫자, 공란 저장 시 삭제)`,
      currentPuuid || ''
    );
    if (input === null) return; // 취소
    const normalized = input.toUpperCase().replace(/[^A-F0-9]/g, '');
    if (normalized && !PUUID_PATTERN.test(normalized)) {
      alert(`PUUID는 32자여야 합니다. (정제 결과: ${normalized.length}자)`);
      return;
    }
    try {
      await updateAdminUserPuuid(loginId, normalized);
      fetchUsers();
    } catch (e) {
      const msg = e?.response?.data || '변경 실패';
      alert(typeof msg === 'string' ? msg : '변경 실패');
    }
  };

  const handleDelete = async (user) => {
    if (user.login_id === myLoginId) {
      alert('본인 계정은 삭제할 수 없습니다.');
      return;
    }
    if (!window.confirm(`${user.user_name || ''}(${user.login_id}) 회원을 삭제하시겠습니까?\n삭제하면 복구할 수 없습니다.`)) return;
    try {
      await deleteAdminUser(user.login_id);
      fetchUsers();
    } catch (e) {
      const msg = e?.response?.data || '삭제 실패';
      alert(typeof msg === 'string' ? msg : '삭제 실패');
    }
  };

  const handleCopyAllPuuids = async () => {
    try {
      const res = await getAdminAllPuuids();
      const list = res.data || [];
      if (list.length === 0) {
        alert('등록된 PUUID가 없습니다.');
        return;
      }
      const text = list.map(u => u.puuid).join('\n');
      await navigator.clipboard.writeText(text);
      alert(`${list.length}명의 PUUID를 클립보드에 복사했습니다.`);
    } catch {
      alert('복사 실패 — 브라우저 권한을 확인하세요.');
    }
  };

  return (
    <div className="sena-page">
      <header className="sena-header">
        <button className="siege-back-btn" onClick={() => navigate('/sena')}>← 메인으로</button>
        <h1 className="sena-header-guild">회원 관리</h1>
      </header>

      <div className="sena-admin-container">
        {/* 필터 탭 + 유틸 */}
        <div className="sena-admin-filters">
          {ROLES.map(r => (
            <button key={r.key} className={`sena-admin-filter ${filter === r.key ? 'active' : ''}`}
              onClick={() => setFilter(r.key)}>
              {r.label}
            </button>
          ))}
          {isAdmin && (
            <button className="sena-admin-filter" style={{ marginLeft: 'auto', background: '#e8734a', color: '#fff', borderColor: '#e8734a' }}
              onClick={handleCopyAllPuuids}>
              📋 PUUID 일괄 복사
            </button>
          )}
        </div>

        {/* 목록 */}
        {loading ? (
          <p className="sena-admin-empty">조회 중...</p>
        ) : users.length === 0 ? (
          <p className="sena-admin-empty">해당 조건의 회원이 없습니다.</p>
        ) : (
          <div className={`sena-admin-list ${isAdmin ? '' : 'no-del'}`}>
            <div className="sena-admin-row header">
              <span className="sena-admin-col id">아이디</span>
              <span className="sena-admin-col name">닉네임</span>
              <span className="sena-admin-col puuid">PUUID</span>
              <span className="sena-admin-col role">등급</span>
              <span className="sena-admin-col action">변경</span>
              {isAdmin && <span className="sena-admin-col del">삭제</span>}
            </div>
            {users.map(user => (
              <div key={user.login_id} className="sena-admin-row">
                <span className="sena-admin-col id">{user.login_id}</span>
                <span className="sena-admin-col name">{user.user_name}</span>
                {isAdmin ? (
                  <span className="sena-admin-col puuid"
                    onClick={() => handlePuuidEdit(user.login_id, user.puuid)}
                    title={user.puuid ? `${user.puuid} (클릭하여 수정)` : '클릭하여 등록'}
                    style={{ cursor: 'pointer', color: user.puuid ? '#6a5040' : '#c88050', textDecoration: user.puuid ? 'none' : 'underline' }}>
                    {user.puuid || '+ 등록'}
                  </span>
                ) : (
                  <span className="sena-admin-col puuid">{user.puuid || '-'}</span>
                )}
                <span className="sena-admin-col role"
                  style={{ color: ROLE_COLORS[user.user_role] || '#555' }}>
                  {ROLE_LABELS[user.user_role] || user.user_role}
                </span>
                <span className="sena-admin-col action">
                  <select value={user.user_role}
                    disabled={!canChangeRole(user)}
                    title={canChangeRole(user) ? '' : '길드마스터는 가입대기·일반 회원만 변경할 수 있습니다.'}
                    onChange={e => handleRoleChange(user.login_id, e.target.value)}>
                    <option value="PENDING">가입대기</option>
                    <option value="USER">일반</option>
                    {isAdmin && <option value="MASTER">길드마스터</option>}
                    {isAdmin && <option value="ADMIN">관리자</option>}
                    {!isAdmin && !canChangeRole(user) && (
                      <option value={user.user_role}>{ROLE_LABELS[user.user_role]}</option>
                    )}
                  </select>
                </span>
                {isAdmin && (
                  <span className="sena-admin-col del">
                    <button type="button" className="sena-admin-del-btn"
                      disabled={user.login_id === myLoginId}
                      title={user.login_id === myLoginId ? '본인 계정은 삭제할 수 없습니다.' : '회원 삭제'}
                      onClick={() => handleDelete(user)}>
                      삭제
                    </button>
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
